import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { getMe } from '../../plugins/infrastructure/api';
import {
  RbacPermission,
  RbacRole,
} from '../../plugins/infrastructure/rbacTypes';

export interface RbacState {
  email: string;
  displayName: string;
  role?: RbacRole;
  permissions: RbacPermission[];
  loading: boolean;
  error?: string;
  refetch: () => void;
}

export const RbacContext = createContext<RbacState | undefined>(undefined);

/**
 * Fetches the caller's role + permissions from GET /me once on mount and again
 * whenever the window regains focus, so a role change made elsewhere takes
 * effect on the next focus/refresh without a forced logout. UI gating derives
 * from this; the backend remains the source of truth on every request.
 */
export function RbacProvider({ children }: { children: ReactNode }) {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [state, setState] = useState<Omit<RbacState, 'refetch'>>({
    email: '',
    displayName: '',
    role: undefined,
    permissions: [],
    loading: true,
    error: undefined,
  });

  const load = useCallback(async () => {
    try {
      const me = await getMe(fetchApi, backendUrl);
      setState({
        email: me.email,
        displayName: me.displayName,
        role: me.role,
        permissions: me.permissions,
        loading: false,
        error: undefined,
      });
    } catch (err: any) {
      setState({
        email: '',
        displayName: '',
        role: undefined,
        permissions: [],
        loading: false,
        error: err.message,
      });
    }
  }, [fetchApi, backendUrl]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  // A fresh object here on every render would re-render every consumer of
  // useRbac() (the sidebar included) whenever *anything* causes this
  // provider to re-render, not just when the RBAC state actually changes.
  const value = useMemo(() => ({ ...state, refetch: load }), [state, load]);

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
}
